import { getFingerprint } from '@nizza/utils';
import IconClose from '@vtex/styleguide/lib/icon/Close';
import React, { useContext, useState } from 'react';
import { useIntl } from 'react-intl';
import { ActionsContext, SettingContext } from '../../../context';
import { useSessionId } from '../../../hooks/useSessionId';
import { nzApiClient } from '@nizza/core';
import styles from './Login.module.css';
import { v4 as uuidv4 } from 'uuid';

interface Props {
  content: string;
  selectedGif: string | undefined;
  setShowLoginWindow: React.Dispatch<React.SetStateAction<boolean>>;
  setUserIsLoggedInChat: React.Dispatch<React.SetStateAction<boolean>>;
  setSendFirstMessage: React.Dispatch<React.SetStateAction<boolean>>;
  setContent: React.Dispatch<React.SetStateAction<string>>;
}

export const Login = ({
  content,
  selectedGif,
  setShowLoginWindow,
  setUserIsLoggedInChat,
  setSendFirstMessage,
  setContent,
}: Props) => {
  const { formatMessage } = useIntl();
  const [username, setUsername] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [errorUsername, setErrorUsername] = useState(false);
  const [errorEmail, setErrorEmail] = useState(false);
  const [disabledBtn, setDisabledBtn] = useState(false);
  const { infoSocket, ivsChatSocket } = useContext(SettingContext);
  const { sessionId } = useSessionId();
  const { socket, setShowLoader } = ivsChatSocket || {};
  const { sendAccountId, emailIsRequired } = infoSocket || {};

  const {
    setting: { account, idLivestreaming },
  } = useContext(ActionsContext);

  const handlerCloseCard = () => {
    setShowLoginWindow(false);

    if (setShowLoader) setShowLoader(false);
  };

  const sendMessage = (username: string) => {
    setTimeout(() => {
      const data = {
        action: 'SEND_MESSAGE',
        content:
          selectedGif || content.replace(/\\/g, '\\\\').replace(/"/g, '\\"'),
        attributes: {
          id: uuidv4(),
          account,
          idEvent: idLivestreaming,
          sessionId: sessionId,
          username,
          type: selectedGif ? 'gif' : 'string',
          fingerprint: getFingerprint(),
          language: navigator.language,
          email,
        },
      };

      socket?.send(JSON.stringify(data));
      setContent('');
    }, 500);
  };

  const emailIsValid = () => {
    const regEmail =
      /^(([^<>()[\]\\.,;:\s@”]+(\.[^<>()[\]\\.,;:\s@”]+)*)|(“.+”))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z_\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    const isEmptyEmail = !(email !== null && email.trim() !== '');

    const isValid = emailIsRequired
      ? regEmail.test(email.trim())
      : isEmptyEmail
      ? true
      : regEmail.test(email.trim());

    setErrorEmail(!isValid);

    return isValid;
  };

  const usernameIsValid = () => {
    const isEmptyUsername = !(username !== null && username.trim() !== '');

    if (isEmptyUsername) {
      setErrorUsername(true);
      return false;
    }

    return true;
  };

  const defineUsername = async () => {
    const { data: response } = await nzApiClient.rest<{
      data: { exist: boolean; number?: number };
    }>({
      path: `/v1/player/${idLivestreaming}/usernameexist`,
      queryParams: {
        account: account,
        username: username,
      },
    });

    return !response.exist ? username : `${username} ${response.number}`;
  };

  const handlerSendDataToChat = async (event: React.SyntheticEvent) => {
    if (setShowLoader === undefined) return;
    setShowLoader(true);
    setDisabledBtn(true);
    event.preventDefault();
    event.persist();
    const isValid = await usernameIsValid();

    if (!isValid || !sendAccountId || !emailIsValid()) {
      setDisabledBtn(false);
      setShowLoader(false);
      return;
    }

    const newUsername = await defineUsername();

    sendAccountId(newUsername, email || '');
    sendMessage(newUsername as string);

    localStorage.setItem(
      'userIsLoggedInChat',
      JSON.stringify({
        value: true,
        id: idLivestreaming,
        username: newUsername,
        email,
      }),
    );
    setShowLoginWindow(false);
    setUserIsLoggedInChat(true);
    setSendFirstMessage(true);
    setShowLoader(false);
  };

  return (
    <div className={styles.loginContainer}>
      <button className={styles.closeCardBtn} onClick={handlerCloseCard}>
        <IconClose size={23} />
      </button>

      <form onSubmit={handlerSendDataToChat} className={styles.loginForm}>
        <div className={styles.inputContainer}>
          <label className={styles.label} htmlFor="content">
            {formatMessage({ id: 'store/live.chat-login' })}
          </label>
        </div>

        <div className={styles.inputContainer}>
          <input
            autoFocus
            maxLength={15}
            placeholder={formatMessage({
              id: 'store/live.chat-login-name-placeholder',
            })}
            id="name"
            name="name"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setUsername(e.target.value)
            }
            value={username}
            autoComplete="off"
            className={`${styles.input} ${
              errorUsername ? styles.inputError : ''
            }`}
          />
          {errorUsername ? (
            <div className={styles.inputErrorMessage}>
              {formatMessage({ id: 'store/live.chat-login-invalid-username' })}
            </div>
          ) : null}
        </div>
        {emailIsRequired && (
          <div className={styles.inputContainer}>
            <input
              placeholder={
                emailIsRequired
                  ? formatMessage({
                      id: 'store/live.chat-login-email-required-placeholder',
                    })
                  : formatMessage({
                      id: 'store/live.chat-login-email-no-required-placeholder',
                    })
              }
              id="email"
              name="email"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setEmail(e.target.value)
              }
              value={email}
              autoComplete="off"
              autoCorrect="off"
              className={`${styles.input} ${
                errorEmail ? styles.inputError : ''
              }`}
            />

            {errorEmail ? (
              <div className={styles.inputErrorMessage}>
                {formatMessage({ id: 'store/live.chat-login-invalid-email' })}
              </div>
            ) : null}
          </div>
        )}

        <button disabled={disabledBtn} type="submit" className={styles.btn}>
          {formatMessage({ id: 'store/live.chat-login-btn' })}
        </button>
      </form>
    </div>
  );
};
